'use client';

import React from 'react';
import { cn, formatDate } from '@/helpers/common';
import { locationFormatter } from '@/helpers/miscellaneous-helpers';
import { TJSJobType } from '@/ts/types';
import SearchForm from '../SearchForm/SearchForm';
import StubMC from './stubs/StubMC';
import { STUB_DEFAULT_TEXTS } from '@/constants/mc/default';
import Link from 'next/link';
import { ISearchParams } from '@/ts/interfaces';
import Pagination from '../Pagination/Pagination';

const CategoryMC = ({
  q,
  l,
  mainText,
  subText,
  jobsList,
  baseUrl,
  page,
  searchParams,
}: {
  q?: string;
  l?: string;
  mainText: string;
  subText: string;
  jobsList: Array<TJSJobType>;
  baseUrl: string;
  page: number;
  searchParams: ISearchParams;
}) => {
  const totalPages = jobsList.length > 20 ? (page < 5 ? 5 : page + 1) : page;

  return (
    <div>
      <div className="flex flex-col items-center mx-auto max-w-[800px]">
        <h1 className="text-2xl leading-9 font-bold text-primary text-center">{mainText}</h1>
        <h2 className="mt-3 text-white text-center">{subText}</h2>
        <div className="w-full">
          <SearchForm
            q={q}
            l={l}
            baseUrl={baseUrl}
            formClassName={{
              main: 'pt-6',
              button:
                'font-bold max-md:text-base md:text-sm text-secondary rounded-lg px-5 hover:bg-main-text-color',
              btnArrow: 'arrow ml-2',
            }}
            searchFieldClassName={{
              input:
                'placeholder-white2 outline outline-transparent border-white2 !rounded-lg focus-visible:border-none focus-visible:outline-primary focus-visible:outline-2',
              suggestions: 'bg-secondary rounded-lg border-2 border-primary',
              suggestionElement: 'hover:bg-primary hover:text-black',
              suggestionElementActive: 'bg-primary text-black',
            }}
            placeholders={{ location: 'Location', keyword: 'Keywords' }}
            scrollElementId="#jobs-list"
          />
        </div>
      </div>
      {jobsList.length > 0 ? (
        <>
          <div className="pb-10 max-md:-mx-4 md:mx-0" id="jobs-list">
            {jobsList.slice(0, 20).map((item, index) => (
              <div
                className={cn(
                  'flex flex-col items-baseline text-sm py-4 px-5 border-b border-solid border-white3',
                  {
                    'border-dashed': index !== jobsList.length - 1,
                  },
                )}
                key={item.id}
                id={item.id}
              >
                <div className="w-full flex justify-between items-start gap-x-4 gap-y-2 flex-wrap">
                  <div>
                    <Link
                      href={item.jobPageUrl}
                      className={cn(
                        'inline items-center text-base font-bold text-list-title-color no-underline hover:underline visited:text-visited-color',
                      )}
                    >
                      {item.title}
                    </Link>
                    <span className="ml-4 text-xs text-page-text-color2">
                      {formatDate({ dateValue: item.date, variant: 3 })}
                    </span>
                    <div className="mt-1">
                      <span className="font-bold text-list-company-color mr-5">{item.company}</span>
                      <span className="text-list-loc-color font-bold">
                        {locationFormatter(item?.location)}
                      </span>
                    </div>
                    <p className="mt-3 text-main-text-color line-clamp-2">{item?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {(+page > 1 || (+page === 1 && jobsList.length > 20)) && (
            <Pagination
              currentPage={+page || 1}
              totalPages={totalPages}
              searchParams={searchParams}
              elementClassName={{
                base: 'h-8 w-8 bg-transparent rounded-lg font-bold hover:bg-primary hover:text-secondary [&>svg>path]:!fill-main-text-color [&>svg>path]:hover:!fill-secondary',
                selected: 'bg-primary !text-secondary',
              }}
            />
          )}
        </>
      ) : (
        <StubMC texts={STUB_DEFAULT_TEXTS} elementClassName={{ mainClassName: 'my-20' }} />
      )}
    </div>
  );
};

export default CategoryMC;
